// assets/stamp-studio.js
// Orchestrates the 3-panel custom-stamp studio wizard: pick a real physical
// size -> finish the design (wraps the freeform stamp-designer-component) ->
// pick which physical stamp is available at that size. Size is picked FIRST
// and drives the editor's die shape via setStampSize(); the studio does NOT
// reimplement any design/rendering logic — it reads the editor's own hidden
// `properties[...]` inputs at hand-off and gates progress on the editor's
// bubbling `stamp:proof-confirmed` event.
import { Component } from '@theme/component';
import { STAMP_SIZES, isSmallSize, getStampSize } from '@theme/stamp-sizes';
import { getTemplate, buildStampThumbSVG } from '@theme/stamp-templates';
import {
  saveDesign,
  pruneOldDesigns,
  getSlots,
  saveSlots,
  getFlow,
  clearFlow,
  FREE_DESIGN_NOTE,
  DATE_ONLY_NOTE,
} from '@theme/stamp-design-store';
import { soloCheckout } from '@theme/stamp-solo-checkout';

// Advisory-only threshold (combined characters across the visible text
// fields) — not a hard limit. Real per-size manufacturing fit constraints
// are a deferred follow-up; this is just a plain-language nudge.
const LONG_TEXT_THRESHOLD = 18;

function escapeHtml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

class StampStudio extends Component {
  /** @type {import('@theme/stamp-sizes').StampSize | null} */
  #selectedSize = null;
  /** @type {boolean} */
  #proofConfirmed = false;
  #lastTextLength = 0;
  /** Set via ?mode=free-design (the "Design it for me" flow): skips the
   *  design step entirely and goes straight from size to product. */
  #freeDesign = false;
  /** A date-only design: the die is a band of rotating wheels, so there is
   *  nothing for the shopper to lay out. Like #freeDesign, this skips the
   *  editor -- see #skipsDesignStep(). */
  #dateOnly = false;
  /** Set via ?product=<handle> — the studio was opened FROM a product page, so
   *  the stamp is already chosen. Step 3 is skipped and the finished design is
   *  handed straight back to that page's design slots. */
  #returnHandle = '';
  #returnSlot = 1;
  #returnTitle = '';

  connectedCallback() {
    super.connectedCallback();
    const params = new URLSearchParams(location.search);
    this.#freeDesign = params.get('mode') === 'free-design';
    this.#returnHandle = params.get('product') || '';
    const slot = parseInt(params.get('slot') || '1', 10);
    this.#returnSlot = Number.isFinite(slot) && slot > 0 ? slot : 1;
    this.#returnTitle = params.get('title') || '';

    if (this.#freeDesign) {
      const dots = this.refs.stepDots ?? [];
      if (dots[1]) dots[1].textContent = '2. Design (by us)';
      if (this.refs.backToDesignBtn) this.refs.backToDesignBtn.textContent = '← Back to size';
    }
    const tpl = getTemplate(params.get('template') || '');
    this.#dateOnly = !!tpl && tpl.shape === 'dater' && tpl.dateMode === 'date-only';
    if (this.#dateOnly) this.#applyDateOnlyMode(tpl);

    if (this.#returnHandle) this.#applyReturnMode();
    pruneOldDesigns();
    this.#buildSizeGrid();
    this.addEventListener('stamp:proof-confirmed', () => this.#onProofConfirmed());
    // The editor reports its total text length as the design changes; drive the
    // small-size advisory off that event instead of reading its inputs.
    this.addEventListener('stamp:design-changed', (e) => {
      this.#lastTextLength = (e.detail && e.detail.textLength) || 0;
      this.#checkSizeAdvisory();
    });
    this.#showPanel('size');

    // A product that knows its own size (carries a `stamp-size:<slug>` tag)
    // passes it through so the shopper isn't asked something we already know.
    const presetSize = params.get('size');
    if (presetSize && !this.#preselectSize(presetSize)) this.#refuseUnknownSize(presetSize);
  }

  #designerEl() {
    return this.querySelector('stamp-designer-component');
  }

  /**
   * True when this run has nothing for the shopper to lay out, so the size step
   * hands straight on to picking a stamp.
   *
   * Two ways to get here: "Design it for me", where we do the artwork off-site,
   * and a date-only stamp, whose die is nothing but the rotating wheels.
   */
  #skipsDesignStep() {
    return this.#freeDesign || this.#dateOnly;
  }

  /**
   * A date-only design has no editor step, so the size panel has to carry the
   * whole story: drop "2. Design" from the stepper and show a sample impression
   * with a real date on it, so the shopper can see what they're buying.
   * @param {import('@theme/stamp-templates').StampTemplate} tpl
   */
  #applyDateOnlyMode(tpl) {
    // Drop the design step and renumber what's left, so the stepper doesn't
    // read "1. Size  3. Choose stamp".
    const dots = this.refs.stepDots ?? [];
    if (dots[1]) dots[1].hidden = true;
    if (dots[2]) dots[2].textContent = '2. Choose stamp';

    const { sizeSample, sizeSampleArt, sizeSubtitle } = this.refs;
    if (sizeSampleArt) {
      sizeSampleArt.innerHTML = buildStampThumbSVG(tpl);
      // The thumbnail's viewBox is square with the die banded across its middle,
      // so left alone it reserves a lot of empty space above and below. Crop to
      // the band by letting the square overflow a wider box. Set inline rather
      // than in the section's {% stylesheet %}: that gets compiled into a cached
      // bundle which can lag a deploy by minutes, and this has to be right the
      // first time a shopper sees it.
      const svg = sizeSampleArt.querySelector('svg');
      if (svg) svg.setAttribute('style', 'width:100%;height:auto;display:block');
      sizeSampleArt.style.cssText =
        'width:100%;max-width:260px;margin-inline:auto;aspect-ratio:5/3;overflow:hidden;display:grid;place-items:center';
    }
    if (sizeSample) sizeSample.hidden = false;
    if (sizeSubtitle) {
      sizeSubtitle.textContent =
        "This stamp prints the date and nothing else, so there's nothing to design, just tell us the size you need.";
    }
  }

  /**
   * Opened from a product page: the stamp is already known, so drop step 3
   * ("Choose stamp") and say which stamp this design is for. The size step is
   * deliberately kept — most products carry no `stamp-size:` tag, so the shopper
   * still has to tell us the die geometry. `?size=` lets a product that DOES
   * know its size skip straight to the design step.
   */
  #applyReturnMode() {
    const dots = this.refs.stepDots ?? [];
    if (dots[2]) dots[2].hidden = true;

    const note = this.refs.contextNote;
    if (note) {
      const name = this.#returnTitle || 'your stamp';
      note.textContent =
        this.#returnSlot > 1
          ? `Designing stamp ${this.#returnSlot} of your ${name} order. When you're done we'll take you straight back.`
          : `Designing for ${name}. When you're done we'll take you straight back to add it to your cart.`;
      note.hidden = false;
    }
  }

  /**
   * Pre-select a size passed in the URL (a product that carries a
   * `stamp-size:<slug>` tag) and jump to the design step.
   * @param {string} value
   */
  #preselectSize(value) {
    const size = getStampSize(value);
    if (!size) return false;
    this.#selectedSize = size;
    if (this.#skipsDesignStep()) {
      // Size already known and nothing to design: go straight to the stamp.
      this.#afterSizeWithoutDesign();
      return true;
    }
    this.#designerEl()?.setStampSize({ shape: size.shape, label: size.label, mm: size.mm });
    this.#showPanel('design');
    this.#checkSizeAdvisory();
    return true;
  }

  /**
   * The finished design as a flat line-item-property map. Shared by both
   * hand-off paths (return-to-product and choose-a-stamp) so they can never
   * drift apart.
   * @returns {Record<string, string>}
   */
  #collectProps() {
    /** @type {Record<string, string>} */
    const props = {};
    if (this.#freeDesign) {
      // No designer element was ever shown/used in this flow -- nothing to
      // scrape. The order-confirmation email keys off this exact property.
      props['Design'] = FREE_DESIGN_NOTE;
    } else if (this.#dateOnly) {
      // Nothing is cut into this die but the date wheels, so there are no
      // editor inputs to scrape and nothing for the workshop to set.
      props['Design'] = DATE_ONLY_NOTE;
    } else {
      const designerEl = this.#designerEl();
      designerEl?.querySelectorAll('[name^="properties\\["]').forEach((el) => {
        const match = /** @type {HTMLInputElement} */ (el).name.match(/^properties\[(.+)\]$/);
        const value = /** @type {HTMLInputElement} */ (el).value;
        if (match && value) props[match[1]] = value;
      });
    }
    if (this.#selectedSize) props['Size'] = this.#selectedSize.label;
    return props;
  }

  /**
   * Which size category the loaded design can actually be cut at.
   *
   * A date stamp's die carries rotating bands, so it can only be made by a
   * dater body — and only the one dater that also takes fixed text can carry a
   * date-with-text design. Everything else (round, rectangle, and the "For"
   * signature designs, which are rectangle dies) maps to its own shape.
   *
   * Returns null when there's no template to go on — a blank canvas, an
   * uploaded artwork or the "design it for me" flow. Those show the ordinary
   * round + rectangle sizes: a freeform design has no rotating date band, so
   * offering a dater body would sell the shopper the wrong stamp.
   * @returns {import('@theme/stamp-sizes').SizeCategory | null}
   */
  #templateCategory() {
    const id = new URLSearchParams(location.search).get('template');
    if (!id) return null;
    const tpl = getTemplate(id);
    if (!tpl) return null;
    if (tpl.shape === 'dater') {
      return tpl.dateMode === 'date-text' ? 'dater-date-text' : 'dater-date-only';
    }
    return tpl.shape === 'rectangle' || tpl.shape === 'for' ? 'rectangle' : 'round';
  }

  /**
   * The categories this visit is allowed to offer, or null for a free browse.
   *
   * The loaded design narrows it first -- a round seal can only be cut on a
   * round die. Failing that, the product the shopper came from narrows it, via
   * the `only=` the gallery and the product page already compute and forward.
   * The studio was ignoring `only=` entirely, which is how someone on a 25mm
   * round stamp was offered every rectangle size we stock and walked away with
   * "30mm (Round)" written on their order.
   *
   * @returns {string[]|null}
   */
  #allowedCategories() {
    const fromTemplate = this.#templateCategory();
    if (fromTemplate) return [fromTemplate];

    const only = new URLSearchParams(location.search).get('only') || '';
    if (only === 'round') return ['round'];
    if (only === 'rectangle') return ['rectangle'];
    // The Liquid fallback can only say "some dater"; both kinds stay open.
    if (only === 'dater') return ['dater-date-only', 'dater-date-text'];
    if (only === 'dater-date-only' || only === 'dater-date-text') return [only];
    return null;
  }

  /**
   * Refuse to guess a die we don't stock.
   *
   * A product can arrive carrying a `stamp-size:` slug that is not in
   * STAMP_SIZES -- a size retired from the picker, or one never added. The old
   * behaviour was to ignore it and show the full list, so the shopper picked
   * something that was not their stamp and that wrong size went onto the order.
   * Saying so is the only honest option: we cannot cut what we cannot size.
   *
   * @param {string} slug the size the product asked for
   */
  #refuseUnknownSize(slug) {
    const grid = this.refs.sizeGrid;
    if (!grid) return;
    const title = this.#returnTitle || 'this stamp';
    grid.innerHTML =
      `<p class="stamp-studio__status">We can't size ${escapeHtml(title)} online yet. Rather than ` +
      'risk cutting it to the wrong die, please get in touch and we will set this one up by hand.</p>';
  }

  #buildSizeGrid() {
    const grid = this.refs.sizeGrid;
    if (!grid) return;
    const allowed = this.#allowedCategories();
    const groups = [
      { key: 'round', label: 'Round' },
      { key: 'rectangle', label: 'Rectangle & square' },
      { key: 'dater-date-only', label: 'Date stamps' },
      { key: 'dater-date-text', label: 'Date stamps' },
    ].filter((g) => (allowed ? allowed.includes(g.key) : g.key === 'round' || g.key === 'rectangle'));
    grid.innerHTML = groups
      .map((g) => {
        const sizes = STAMP_SIZES.filter((s) => s.category === g.key);
        if (!sizes.length) return '';
        const btns = sizes
          .map(
            (s) =>
              `<button type="button" class="stamp-studio__size-btn" data-size="${s.value}" on:click="/handleSizePick">${escapeHtml(s.label)}</button>`
          )
          .join('');
        return `<div class="stamp-studio__size-group"><p class="stamp-studio__size-grouplabel">${g.label}</p><div class="stamp-studio__size-row">${btns}</div></div>`;
      })
      .join('');
  }

  /** @param {Event} event */
  handleSizePick(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-size]');
    if (!btn) return;
    const size = STAMP_SIZES.find((s) => s.value === btn.dataset.size);
    if (!size) return;
    this.#selectedSize = size;
    this.querySelectorAll('.stamp-studio__size-btn').forEach((b) => b.classList.toggle('is-selected', b === btn));
    if (this.#skipsDesignStep()) {
      this.#afterSizeWithoutDesign();
      return;
    }
    // Drive the editor's die shape/proportions from the chosen physical size.
    this.#designerEl()?.setStampSize({ shape: size.shape, label: size.label, mm: size.mm });
    this.#showPanel('design');
    this.#checkSizeAdvisory();
  }

  /**
   * The size pick IS the whole wizard when there's nothing to lay out.
   *
   * Checking #returnHandle first matters: otherwise this showed the "choose your
   * stamp" step that #applyReturnMode had just hidden, and the eventual hand-off
   * went through handleSelectProduct, which pinned slot 1 and silently dropped
   * whichever slot actually asked.
   */
  #afterSizeWithoutDesign() {
    if (this.#returnHandle) {
      this.#handOff(this.#returnHandle, this.#returnSlot);
      return;
    }
    this.#showPanel('product');
    this.#loadProducts();
  }

  /**
   * Plain-language, non-blocking nudge when a small size is paired with
   * comparatively long text — not a hard character limit (see
   * LONG_TEXT_THRESHOLD's comment above).
   */
  #checkSizeAdvisory() {
    const el = this.refs.sizeAdvisory;
    if (!el) return;
    if (!this.#selectedSize || !isSmallSize(this.#selectedSize)) {
      el.hidden = true;
      return;
    }
    if ((this.#lastTextLength || 0) >= LONG_TEXT_THRESHOLD) {
      el.textContent = `This is a small stamp (${this.#selectedSize.label}), you may want to shorten your text so it fits comfortably.`;
      el.hidden = false;
    } else {
      el.hidden = true;
    }
  }

  handleBackToSize() {
    this.#showPanel('size');
  }

  handleBackToDesign() {
    this.#showPanel(this.#freeDesign ? 'size' : 'design');
  }

  #onProofConfirmed() {
    this.#proofConfirmed = true;
    const btn = this.refs.continueToProductBtn;
    if (btn) {
      btn.disabled = false;
      btn.textContent = this.#returnHandle ? 'Use this design →' : 'Choose your stamp →';
    }
  }

  handleContinueToProduct() {
    if (!this.#proofConfirmed || !this.#selectedSize) return;
    // Opened from a product page: skip "choose your stamp" entirely and hand the
    // design straight back to the slot it was started from.
    if (this.#returnHandle) {
      this.#handOff(this.#returnHandle, this.#returnSlot);
      return;
    }
    this.#showPanel('product');
    this.#loadProducts();
  }

  /**
   * Save the finished design, put it in its slot, and decide where the shopper
   * goes next. The single hand-off point for every route into this studio.
   *
   * Three destinations:
   *   - no "Buy now" flow running -> the product page, as always
   *   - flow with stamps still undesigned -> straight on to the next one
   *   - flow complete -> add the lot to the cart and go to checkout
   *
   * The middle case routes through the design gallery rather than bouncing off
   * the product page each time; at quantity 5 that is four full product-page
   * loads the shopper never sees.
   *
   * @param {string} handle
   * @param {number} slot
   */
  #handOff(handle, slot) {
    const props = this.#collectProps();
    const id = saveDesign(props);

    // Record the design against its slot before routing, so the decision below
    // (and the product page, whenever it is next seen) reads one shared truth.
    const saved = getSlots(handle);
    const ids = saved ? saved.ids.slice() : [];
    const index = slot > 0 ? slot - 1 : 0;
    while (ids.length <= index) ids.push(null);
    ids[index] = id;
    const sameForAll = saved ? saved.sameForAll : false;
    saveSlots(handle, { sameForAll, ids });

    const flow = getFlow(handle);
    if (flow && flow.mode === 'buynow') {
      while (ids.length < flow.total) ids.push(null);
      saveSlots(handle, { sameForAll, ids });

      const nextEmpty = ids.slice(0, flow.total).findIndex((entry) => !entry);
      if (nextEmpty !== -1) {
        window.location.href =
          `/pages/stamp-designs?product=${encodeURIComponent(handle)}` +
          `&slot=${encodeURIComponent(String(nextEmpty + 1))}` +
          `&title=${encodeURIComponent(this.#returnTitle || '')}`;
        return;
      }

      this.#checkout(handle, flow, ids.slice(0, flow.total));
      return;
    }

    this.#toProductPage(handle, slot, id, props);
  }

  /**
   * Every stamp in a "Buy now" run is designed — go and pay for them.
   *
   * Only for this product, the same as pressing Buy now on a page where every
   * slot was already filled: a run that started from one stamp shouldn't end up
   * billing whatever else the shopper left in the cart.
   *
   * @param {string} handle
   * @param {{ variantId: string, total: number }} flow
   * @param {(string|null)[]} ids
   */
  async #checkout(handle, flow, ids) {
    try {
      const bought = await soloCheckout({ variantId: flow.variantId, designIds: ids });
      if (!bought) throw new Error('checkout unavailable');
      clearFlow(handle);
      saveSlots(handle, { sameForAll: false, ids: [] });
    } catch (e) {
      // Never strand someone holding finished designs: the product page can
      // still show every slot and add them by the normal route.
      this.#toProductPage(handle, 1, '', {});
    }
  }

  /**
   * Navigate to the product page, which picks the design up by id. `d=` carries
   * the text-only subset as a belt-and-braces fallback for when localStorage is
   * unavailable (private browsing) — it is safe at any URL length because the
   * heavy `_`-prefixed props are filtered out.
   * @param {string} handle
   * @param {number} slot
   * @param {string} id
   * @param {Record<string,string>} props
   */
  #toProductPage(handle, slot, id, props) {
    if (!id) {
      window.location.href = `/products/${handle}`;
      return;
    }

    /** @type {Record<string, string>} */
    const textProps = {};
    Object.keys(props).forEach((key) => {
      if (!key.startsWith('_')) textProps[key] = props[key];
    });

    const url =
      `/products/${handle}?design=${encodeURIComponent(id)}` +
      `&slot=${encodeURIComponent(String(slot))}` +
      `&d=${encodeURIComponent(JSON.stringify(textProps))}`;
    window.location.href = url;
  }

  /** @param {'size'|'design'|'product'} name */
  #showPanel(name) {
    const { sizePanel, designPanel, productPanel, stepDots } = this.refs;
    if (sizePanel) sizePanel.hidden = name !== 'size';
    if (designPanel) designPanel.hidden = name !== 'design';
    if (productPanel) productPanel.hidden = name !== 'product';
    const order = ['size', 'design', 'product'];
    (stepDots ?? []).forEach((el, i) => el.classList.toggle('is-active', i <= order.indexOf(name)));
  }

  async #loadProducts() {
    const size = this.#selectedSize;
    const { productGrid, productSubtitle } = this.refs;
    if (!size || !productGrid) return;
    if (productSubtitle) productSubtitle.textContent = `Stamps available in ${size.label}`;
    productGrid.innerHTML = '<p class="stamp-studio__status">Loading stamps…</p>';
    try {
      const res = await fetch(`/collections/${size.collectionHandle}/products.json`);
      if (!res.ok) throw new Error('bad response');
      const data = await res.json();
      const products = Array.isArray(data.products) ? data.products : [];
      if (products.length === 0) {
        productGrid.innerHTML =
          '<p class="stamp-studio__status">Nothing available at this size yet, check back soon, or go back and pick a different size.</p>';
        return;
      }
      // Nothing to design and exactly one stamp made at this size: asking the
      // shopper to "choose" from a list of one is a step that only ever has one
      // answer, so take them to it. A date-only size is always this case today.
      if (this.#skipsDesignStep() && products.length === 1 && !this.#freeDesign) {
        this.#handOff(products[0].handle, this.#returnSlot);
        return;
      }
      productGrid.innerHTML = products
        .map((p) => {
          const price = p.variants?.[0]?.price ?? '';
          return `
            <div class="stamp-studio__product-card">
              <p class="stamp-studio__product-title">${escapeHtml(p.title)}</p>
              <p class="stamp-studio__product-price">₹${escapeHtml(price)}</p>
              <div class="stamp-studio__product-desc">${p.body_html || ''}</div>
              <button type="button" class="stamp-studio__product-select" data-handle="${escapeHtml(p.handle)}" on:click="/handleSelectProduct">Select this stamp</button>
            </div>`;
        })
        .join('');
    } catch (e) {
      productGrid.innerHTML =
        '<p class="stamp-studio__status">Something went wrong loading stamps for this size. Please try again.</p>';
    }
  }

  /** @param {Event} event */
  handleSelectProduct(event) {
    const btn = /** @type {HTMLElement} */ (event.target)?.closest?.('[data-handle]');
    const handle = btn?.dataset.handle;
    if (!handle) return;
    // #returnSlot defaults to 1, so this is only different from the old
    // hard-coded 1 when a slot was actually named in the URL -- which is exactly
    // the case that used to lose it.
    this.#handOff(handle, this.#returnSlot);
  }
}

if (!customElements.get('stamp-studio-component')) {
  customElements.define('stamp-studio-component', StampStudio);
}
